package com.veldekar.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.toAttrs
import com.veldekar.HeadlineTextStyle
import com.veldekar.charts.ChartResources
import com.veldekar.components.layouts.PageLayoutData
import com.veldekar.components.widgets.ChartJs
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text

@InitRoute
fun initSessionBarChartPage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Session Chart",
            ogTitle = "Veldekar Session Chart",
            ogDescription = "Bar chart showing the number of D&D sessions per season over time.",
            ogImage = "session-chart-preview.png",
            ogImageWidth = 1200,
            ogImageHeight = 630,
            ogImageAlt = "Bar chart showing sessions per season"
        )
    )
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun SessionChartPage() {
    val chartDataList = ChartResources.sessionsByYear()
    
    Column(Modifier.fillMaxWidth().padding(2.cssRem).gap(4.cssRem)) {
        
        // 1. Chart.js Multi-line X-Axis
        val allLabels = chartDataList.flatMap { data ->
            val shortYear = data.year % 100
            listOf(
                "['W', '${data.year}']",
                "['Spr', '']",
                "['Sum', '']",
                "['Fall', '']"
            )
        }
        val allData = chartDataList.flatMap { data ->
            listOf(data.winter, data.spring, data.summer, data.fall)
        }
        val allFullTitles = chartDataList.flatMap { data ->
            listOf(
                "'Winter ${data.year}'",
                "'Spring ${data.year}'",
                "'Summer ${data.year}'",
                "'Fall ${data.year}'"
            )
        }

        // Drop trailing zero-session seasons so we don't display future seasons
        val lastNonZeroIndex = allData.indexOfLast { it > 0 }
        val endIndex = if (lastNonZeroIndex >= 0) lastNonZeroIndex + 1 else allData.size
        
        val multiLineLabels = allLabels.take(endIndex).joinToString(",")
        val multiLineData = allData.take(endIndex).joinToString(",")
        val fullTitles = allFullTitles.take(endIndex).joinToString(",")
        
        // Define markers for significant events
        val minYear = chartDataList.firstOrNull()?.year ?: 0
        val markers = listOf(
            // is this right? why did we call off sessions all summer if this was over in June?
            // Triple(2025, 1, "G7 in Canada"), // Example: Year, Season Index (0=W, 1=Spr, 2=Sum, 3=Fall), Label
            Triple(2026, 2, "World Cup")
        )

        val annotationsJson = markers.mapIndexedNotNull { i, marker ->
            val (year, seasonIndex, label) = marker
            if (year < minYear) return@mapIndexedNotNull null
            
            val index = (year - minYear) * 4 + seasonIndex
            // language=javascript
            """
                line$i: {
                    type: 'line',
                    scaleID: 'x',
                    value: $index,
                    borderColor: '#ef4444',
                    borderWidth: 2,
                    borderDash: [6, 6],
                    label: {
                        content: '$label',
                        display: true,
                        position: 'start',
                        backgroundColor: 'rgba(239, 68, 68, 0.9)'
                    }
                }
            """
        }.joinToString(",\n")

        // language=javascript
        val multiLineConfig = """
            {
                type: 'bar',
                data: {
                    labels: [$multiLineLabels],
                    datasets: [{
                        label: 'Sessions',
                        data: [$multiLineData],
                        backgroundColor: '#3b82f6'
                    }]
                },
                options: {
                    responsive: true,
                    scales: {
                        x: {
                            ticks: {
                                callback: function(value, index, values) {
                                    let label = this.getLabelForValue(value);
                                    let isArray = Array.isArray(label);
                                    let season = isArray ? label[0] : label;
                                    let year = isArray ? label[1] : '';
                                    if (window.innerWidth >= 768) {
                                        if (season === 'W') season = 'Winter';
                                        if (season === 'Spr') season = 'Spring';
                                        if (season === 'Sum') season = 'Summer';
                                    }
                                    return isArray ? [season, year] : season;
                                }
                            },
                            grid: {
                                color: function(context) {
                                    if (context.index % 4 === 0) {
                                        return window.Chart.defaults.borderColor;
                                    }
                                    return 'transparent';
                                }
                            }
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        title: { display: false },
                        tooltip: {
                            callbacks: {
                                title: function(context) {
                                    const titles = [$fullTitles];
                                    return titles[context[0].dataIndex];
                                }
                            }
                        },
                        annotation: {
                            annotations: {
                                $annotationsJson
                            }
                        }
                    }
                }
            }
        """.trimIndent()
        
        ChartSection("Sessions per Season") {
            ChartJs(multiLineConfig)
        }
    }
}

@Composable
fun ChartSection(title: String, content: @Composable () -> Unit) {
    Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
        H2(HeadlineTextStyle.toAttrs()) {
            Text(title)
        }
        content()
    }
}
